import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import spCommissionReportService, {
  type SpCommissionReportRow,
} from '../../services/spCommissionReportService';
import userAccountService from '../../services/userAccountService';

function periodLabel(row: SpCommissionReportRow) {
  if (row.periodMonth) {
    return `${row.periodYear}-${String(row.periodMonth).padStart(2, '0')}`;
  }
  if (row.periodQuarter) {
    return `${row.periodYear} Q${row.periodQuarter}`;
  }
  return String(row.periodYear);
}

const SpCommissionReportsPanel: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [rows, setRows] = useState<SpCommissionReportRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTitle, setPreviewTitle] = useState('');
  const [previewCsv, setPreviewCsv] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [downloadId, setDownloadId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const links = await userAccountService.getReferralLinks();
        if (!links.isMultiReferralPartner) {
          if (!cancelled) setVisible(false);
          return;
        }
        if (!cancelled) setVisible(true);
        const reports = await spCommissionReportService.list();
        if (!cancelled) setRows(reports);
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load commission reports');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!visible) return null;

  const openPreview = async (row: SpCommissionReportRow) => {
    setPreviewOpen(true);
    setPreviewTitle(`${row.category} ${periodLabel(row)}`);
    setPreviewCsv(null);
    setPreviewLoading(true);
    setDownloadId(row.statementId);
    try {
      setPreviewCsv(await spCommissionReportService.getCsvText(row.statementId));
    } catch {
      setPreviewCsv(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        Commission Reports
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Download audit CSVs for your Strategic Partner commission statements (monthly consumer
        payouts and quarterly category batches).
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <CircularProgress size={24} />
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Period</TableCell>
              <TableCell>Category</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.statementId}>
                <TableCell>{periodLabel(row)}</TableCell>
                <TableCell>{row.category}</TableCell>
                <TableCell align="right">{row.totalAmount.toFixed(4)}</TableCell>
                <TableCell>{row.deliveryStatus}</TableCell>
                <TableCell>
                  <Button size="small" onClick={() => openPreview(row)}>
                    View CSV
                  </Button>
                  <Button
                    size="small"
                    onClick={() =>
                      spCommissionReportService.downloadCsv(
                        row.statementId,
                        `${row.category}-${periodLabel(row)}.csv`
                      )
                    }
                  >
                    Download
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>No commission reports yet.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>{previewTitle}</DialogTitle>
        <DialogContent dividers>
          {previewLoading && <Typography color="text.secondary">Loading…</Typography>}
          {!previewLoading && previewCsv && (
            <pre
              style={{
                margin: 0,
                fontSize: 12,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxHeight: '60vh',
                overflow: 'auto',
                background: '#f5f5f5',
                padding: 12,
              }}
            >
              {previewCsv}
            </pre>
          )}
        </DialogContent>
        <DialogActions>
          {downloadId && (
            <Button
              onClick={() =>
                spCommissionReportService.downloadCsv(downloadId, `${previewTitle}.csv`)
              }
            >
              Download
            </Button>
          )}
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SpCommissionReportsPanel;
